import React from "react";

export default function TeslaModelY({ activeSubTab, handleSubTabClick, carLabel }) {
  const subTabs = [
    { id: "charging", label: "Charging" },
    { id: "driving", label: "Driving" },
    { id: "features", label: "Features" },
    { id: "faq", label: "FAQ" },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <h1 className="text-3xl font-extrabold text-gray-900 pl-3 border-l-4 border-blue-600">
        {carLabel}
      </h1>

      {/* Sub-tabs (desktop only) */}
      <div className="hidden md:flex gap-2 flex-wrap">
        {subTabs.map((sub) => (
          <button
            key={sub.id}
            onClick={() => handleSubTabClick("modelY", sub.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition ${activeSubTab === sub.id ||
              (!activeSubTab && sub.id === "charging")
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            {sub.label}
          </button>
        ))}
      </div>

      {/* Charging */}
      {(!activeSubTab || activeSubTab === "charging") && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🔌 Home Charging (Level 1)</h2>
            <p className="text-gray-700">
              A Level 1 NACS charger (standard wall outlet) is included. It's the slowest option (~3–4 days for a full charge), but it works anywhere and is the cheapest way to top up overnight. The Model Y has a larger battery than the Model 3, so Level 1 charging takes longer. <br />
              <h3 className="text-lg font-semibold mt-3">Location of Charger</h3>
              This charger will be located in the frunk (front trunk) storage compartment. You can access it by opening the frunk via the touchscreen or physical button on the door.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🚀 Tesla Superchargers</h2>
            <p className="text-gray-700">
              Superchargers are the best way to charge if you need to recharge quickly. The Model Y uses Tesla's NACS connector and has access to the Supercharger network. Expect a 0%-80% charge (or around 175 miles of range) in ~30 minutes at a V2 Supercharger. <br />
              Feel free to drive up and plug in to begin charging immediately as all of the payment information is linked to the car and not needed before charging begins!
              <h3 className="text-lg font-semibold mt-3">Cost</h3>
              <b>Any charges incurred via Tesla Superchargers will be billed to you after your rental period is complete via a separate invoice.</b> Supercharging costs vary by location and sometimes even time of day. Expect to pay around $25-$35 for a full charge from 0-100%. <br />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">⚡ Generic Public Charging (Level 2+)</h2>
            <p className="text-gray-700">
              You cannot use most public Level 2 chargers outside the Tesla Supercharging network without a compatible adapter. Support for NACS is growing as it has become the new standard in the US but, most stations still use the older J1772 or CCS connector.
            </p>
          </div>
        </div>
      )}

      {/* Driving */}
      {activeSubTab === "driving" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🚗 Starting & Shifting</h2>
            <p className="text-gray-700">
              Unlock with the key card or phone app (phone app available upon request for rentals that are 2 weeks or longer!). If using the keycard, you can place it just behind the cup holders to start the car. Press brake and shift using the right stalk. <br />
              To lock/unlock the car, tap the key card on the pillar behind the driver's side window (there are picture instructions on the keycard).
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🧭 Autopilot Basics</h2>
            <p className="text-gray-700">
              Push the right stalk down twice in quick succession to engage Autopilot. Keep hands on the wheel at all times. Autopilot is available by default. The Model Y's additional sensors provide enhanced awareness compared to the Model 3.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🤖 Full Self-Driving</h2>
            <p className="text-gray-700">
              If enabled, FSD adds features like Navigate on Autopilot, Auto Lane Change, and Smart Summon. Use with caution and always supervise. FSD is available as a paid extra.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🔋 Range Tips</h2>
            <p className="text-gray-700">
              Rated range ~325 miles. Cold weather and high speeds reduce range. <br />
              <h3 className="text-lg font-semibold mt-3">Tips to increase range:</h3>
              - Reducing your heat/AC usage in favor of seat heaters or windows can help conserve energy.<br />
              - Avoid hard braking in favor of regenerative braking (one-pedal driving) can help maximize range. This is enabled by default by Tesla. <br />
              - Driving at moderate speeds and avoiding the highway/interstate (high speeds over long periods) can improve efficiency.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">⚙️ AWD Considerations</h2>
            <p className="text-gray-700">
              The Model Y Long Range comes with Dual Motor AWD (all-wheel drive), which provides excellent traction in snow and wet conditions. The system automatically adjusts power distribution between front and rear motors. You can see real-time motor output on the touchscreen under Controls > Service > Power.
            </p>
          </div>
        </div>
      )}

      {/* Features */}
      {activeSubTab === "features" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🚗 Car Specs</h2>
            <p className="text-gray-700">
              2021 Tesla Model Y (Long Range - Pearl White Multi-Coat)<br />
              - Range: ~325 miles<br />
              - 0-60 mph: 4.8 seconds<br />
              - Top Speed: 145 mph<br />
              - 346 hp (dual motor AWD system)<br />
              - Battery Size: 75–81 kWh<br />
              - Seating: 5 passengers (with cargo space for additional gear)
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">📱 Touchscreen</h2>
            <p className="text-gray-700">
              Central 15" touchscreen controls climate, navigation, media, and vehicle settings. <br />
              <b>Android Auto/Apple Carplay are unfortunately not available in any Tesla vehicle by default!</b>
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🎶 Entertainment</h2>
            <p className="text-gray-700">
              Built‑in streaming apps, Bluetooth, and premium audio system with enhanced acoustics compared to earlier Tesla models.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">📦 Storage</h2>
            <p className="text-gray-700">
              Spacious 28 cu ft cargo area (behind rear seats) plus frunk (front trunk, accessible via touchscreen) for additional storage. The Model Y provides significantly more usable space than the Model 3, making it ideal for trips with luggage or equipment.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-2">🌟 Unique Model Y Features</h2>
            <p className="text-gray-700">
              - <b>Falcon Wing Doors:</b> The rear doors open upward, making it easier to load cargo and passengers in tight parking spaces. <br />
              - <b>Heated Steering Wheel:</b> Perfect for cold Wisconsin mornings. <br />
              - <b>Panoramic Roof:</b> Provides excellent visibility and an open-air feel. <br />
              - <b>All-Wheel Drive:</b> Dual motors provide superior traction and handling in all weather conditions.
            </p>
          </div>
        </div>
      )}

      {/* FAQ */}
      {activeSubTab === "faq" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">❓ Tesla Model Y FAQ</h2>
            <div className="divide-y divide-gray-200">
              <div className="py-3">
                <p className="font-semibold">Can I use non‑Tesla chargers?</p>
                <p className="text-gray-700">
                  Only with a J1772 adapter for Level 2 stations. (Not included)
                </p>
              </div>
              <div className="py-3">
                <p className="font-semibold">How do I open the Falcon Wing doors?</p>
                <p className="text-gray-700">
                  The rear Falcon Wing doors open automatically when you approach the car or can be opened via the touchscreen. They close automatically or can be manually closed. Be aware of the door clearance when opening in tight spaces.
                </p>
              </div>
              <div className="py-3">
                <p className="font-semibold">How do I engage Autopilot?</p>
                <p className="text-gray-700">
                  Push the right stalk down twice in quick succession to engage Autopilot. Keep hands on the wheel at all times.
                </p>
              </div>
              <div className="py-3">
                <p className="font-semibold">How do I pay for Tesla Supercharging?</p>
                <p className="text-gray-700">
                  Any charges incurred via Tesla Superchargers will be billed to you after your rental period is complete via a separate invoice - no payment information required! Just back into the stall and plug in to begin charging.
                </p>
              </div>
              <div className="py-3">
                <p className="font-semibold">How do I use Android Auto/Apple Carplay?</p>
                <p className="text-gray-700">
                  Unfortunately, Android Auto and Apple Carplay are not supported. You can use Bluetooth or USB to connect your phone for calls and music.
                </p>
              </div>
              <div className="py-3">
                <p className="font-semibold">Is the Model Y good in snow?</p>
                <p className="text-gray-700">
                  Yes! The dual motor AWD system provides excellent winter performance. The vehicle automatically distributes power between front and rear motors for optimal traction. Reduce speed on icy roads and take advantage of the heated steering wheel and seat warmers.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
